import React, { memo, useEffect, useMemo, useState } from "react";
import {
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { Col, Row } from "antd";
import Acent from "components/Acent";
import TextEditor from "components/TextEditor";
import MatchMakingAudio from "components/Image/MatchMakingAudio";
import MatchMakingImage from "components/Image/MatchMakingImage";
import { get, map } from "lodash";
import styled from "styled-components";
import { DeleteModal } from "components/Dialog";
import MatchMakingText from "components/Image/MatchMakingText";
import { useTabletDetection } from 'containers/chat/hooks';

const AssignmentDescription = styled.span`
  color: #6c757d;
  display: block;
  font-size: 12px;
  font-weight: 400;
  line-height: 17px;
`;

const Detail = styled(AssignmentDescription)`
  color: #495057;
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  margin-top: ${(p) => (p.$first ? "0px" : "24px")};
`;

const AudioFieldsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const MatchMaking = ({
  disabled,
  index,
  question,
  optionRefs,
  onChangeType,
  onAddMoreOption,
  onchangeOptionMatch,
  onDeleteMatchOption,
  loading,
  colorSchema,
  setButtonDisabled = (value) => null,
  isEditing = false,
  showImagePicker,
  onMediaUploaded,
}) => {
  const [deleteModal, setDeleteModal] = useState(false);
  const [checkedRadio, setCheckedRadio] = useState("");
  const [pendingChangeField, setPendingChangeField] = useState(null);
  const [hasChanged, setHasChanged] = useState(false);
  const isTablate = useTabletDetection();

  const handleDeleteModalToggle = (open) => {
    if (!open) {
      setPendingChangeField(null);
      setCheckedRadio("");
    }
    setDeleteModal(open);
  };



  useEffect(() => {
    if(isEditing){
      setHasChanged(true);
    }
  },[isEditing])

  const MatchComponent = useMemo(() => {
    return (
      {
        Image: MatchMakingImage ,
        Audio: MatchMakingAudio,
        Text: MatchMakingText,
        ImageAndAudio: ({ source, audio, handlePicture }) => (
          <div>
            <MatchMakingImage
              source={source}
              handlePicture={(v) => handlePicture(v, "image")}
              setButtonDisabled={(value) => setButtonDisabled(value)}
              imageClassName={`assignment-image ${isTablate ? "assignmentTabImg" : ""}`} 
              containerClassName="assignment-ebook"
              style={{marginTop: source ? (window.innerWidth >= 1024 ? "130px" : "85px") : "" }}
              showImagePicker={showImagePicker}
            />
            <MatchMakingAudio
              source={audio}
              handlePicture={(v) => handlePicture(v, "audio")}
              onMediaUploaded={onMediaUploaded}
              setButtonDisabled={(value) => setButtonDisabled(value)}
            />
          </div>
        ),
      }[question.matchType] || null
    );
  }, [question.matchType, question.questionType, onMediaUploaded, isTablate, showImagePicker, setButtonDisabled]);



  const deleteCustomAssignment = async () => {
    if (pendingChangeField === "matchType") {
      onChangeType(checkedRadio, "matchType", index);
    } else {
      onChangeType(checkedRadio, "questionType", index);
      onChangeType(checkedRadio, "matchType", index);
    }
    setHasChanged(false);
    handleDeleteModalToggle(false);
  };

  const handleBlur = (e) => {
    const inputValue = e.target.value.trim();
    if (inputValue === "") {
      setHasChanged(false);
    }
  };
  
  

  return (
    <>
      <DeleteModal
        message={`Are you sure you want to change the question type?`}
        deleteToggle={deleteModal}
        setDeleteToggle={handleDeleteModalToggle}
        onDelete={deleteCustomAssignment}
        buttonTitle={`yes`}
        loading={loading}
        // colorSchema={colorSchema}
      />
      <div className="category-type-radio-type">
        {/* New Question Type */}
        <Detail $first>Question Type</Detail>
        <FormControl>
          <RadioGroup
            row
            onChange={(e) => {
              if (hasChanged) {
                setPendingChangeField("questionType");
                setCheckedRadio(e.target.value);
                setDeleteModal(true);
              } else {
                onChangeType(e.target.value, "questionType", index);
                onChangeType(e.target.value, "matchType", index);
              }
            }}
            aria-labelledby="category-type-radio-buttons-group-label"
            name={`match-making-question-type-${index}`}
            style={{ gap: "20px" }}
            value={question.questionType || "Text"}
          >
            <FormControlLabel
              disabled={disabled}
              value="Text"
              control={<Radio />}
              label="Text"
              style={{ marginRight: "10px" }}
            />
            <FormControlLabel
              disabled={disabled}
              value="Image"
              control={<Radio />}
              label="Image"
              style={{ marginRight: "10px" }}
            />
            <FormControlLabel
              disabled={disabled}
              value="Audio"
              control={<Radio />}
              label="Audio"
              style={{ marginRight: "10px" }}
            />
            
          </RadioGroup>
        </FormControl>

        <Detail>Media Type</Detail>
        <FormControl>
          <RadioGroup
            row
            onChange={(e) => {
              if (hasChanged) {
                setPendingChangeField("matchType");
                setCheckedRadio(e.target.value);
                setDeleteModal(true);
              } else {
                onChangeType(e.target.value, "matchType", index); 
              } 
            }}
            aria-labelledby="category-type-media-type-radio-label"
            name={`match-making-media-type-${index}`}
            style={{ gap: "20px" }}
            value={question.matchType ?? "Text"}
          >
            {(question.questionType === "Text" || question.questionType === undefined || question.questionType === "Image") && <FormControlLabel
              disabled={disabled}
              value="Text"
              control={<Radio />}
              label="Text"
              style={{ marginRight: "10px" }}
            />}
            {(question.questionType === "Text" || question.questionType === undefined) && <FormControlLabel
              disabled={disabled}
              value="ImageAndAudio"
              control={<Radio />}
              label="Image & Audio"
              style={{ marginRight: "10px" }}
            />}
            <FormControlLabel
              disabled={disabled}
              value="Image"
              control={<Radio />}
              label="Image"
              style={{ marginRight: "10px" }}
            />
            <FormControlLabel
              disabled={disabled}
              value="Audio"
              control={<Radio />}
              label="Audio"
              style={{ marginRight: "10px" }}
            />
          </RadioGroup>
        </FormControl>
        {map(get(question, "options", [""]), (option, optId) => {
          const optionss = question?.options || [""];
          return (
            <div className="seprateSpace">
              <Row
                key={optId}
                style={{
                  marginBottom: "8px",
                  display: "flex",
                  gap: "16px",
                  alignItems: "center",
                }}
              >
                {/* Question Column */}
                <Col style={{ flex: 1 }}>
                  <Row justify={question.questionType !== "Text" ? "space-between" : "end"} style={{ width: "100%"}}>

                    {question.questionType !== "Text" ? <h1>Question {optId + 1}</h1> : <></>}
                    <Row justify={"space-between"} align={"center"}>
                    {get(question, "options", []).length > 1 && (
                      <Col
                        className="delete-button"
                        span={2}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <IconButton
                          disabled={disabled}
                          onClick={() => {
                            onDeleteMatchOption(index, optId);
                          }}
                          style={{ padding: 0, marginBottom: 10 }}
                        >
                          <Delete color="secondary" />
                        </IconButton>
                      </Col>
                    )}
                    </Row>
                  </Row>
              {(question.questionType === "Text" || question.questionType === undefined) && (
                <>
                  <label style={{ marginBottom: 8, fontSize: 12, fontWeight: 400, display: "block" }}>
                    Question {optId + 1}*
                  </label>
                  <TextEditor
                    handleChange={(value) => {
                      setHasChanged(true);
                      onchangeOptionMatch(index, optId, value, "item");
                    }}
                    value={optionss[optId]?.item || ""}
                    placeholder="Type your question here..."
                    autoFocus={false}
                    disabled={disabled}
                    containerClassName="match-making-text-field"
                  />
                </>
              )}
                  {
                    question.questionType === "Image" && (
                      <MatchMakingImage
                        source={option?.questionImage}
                        audio={option?.match_audio || ""}
                        value={option?.questionImage}
                        matchType={question.matchType}
                        onChange={(v) => {
                          setHasChanged(true);
                          onchangeOptionMatch(index, optId, v, "questionImage");
                        }}
                        handlePicture={(v) => {
                          setHasChanged(true);
                          onchangeOptionMatch(index, optId, v, "questionImage");
                        }}
                        showImagePicker={showImagePicker}
                      />
                    )
                  }
                  {
                    question.questionType === "Audio" && question.matchType !== "Audio" && (
                      <MatchMakingAudio
                        source={option?.questionAudio}
                        handlePicture={(v) => {
                          setHasChanged(true);
                          onchangeOptionMatch(index, optId, v, "questionAudio");
                        }}
                        onMediaUploaded={onMediaUploaded}
                        setButtonDisabled={(value) => setButtonDisabled(value)}
                        audioRole="question"
                        sectionLabel="Question audio"
                      />
                    )
                  }
                </Col>
              </Row>
              {/* /Answer Column */}
              <div>
                {question.questionType === "Audio" && question.matchType === "Audio" ? (
                  <AudioFieldsStack>
                    <MatchMakingAudio
                      source={option?.questionAudio}
                      handlePicture={(v) => {
                        setHasChanged(true);
                        onchangeOptionMatch(index, optId, v, "questionAudio");
                      }}
                      onMediaUploaded={onMediaUploaded}
                      setButtonDisabled={(value) => setButtonDisabled(value)}
                      audioRole="question"
                      sectionLabel="Question audio"
                    />
                    <MatchMakingAudio
                      source={option?.match_item}
                      handlePicture={(v) => {
                        setHasChanged(true);
                        onchangeOptionMatch(index, optId, v, "match_item");
                      }}
                      onMediaUploaded={onMediaUploaded}
                      setButtonDisabled={(value) => setButtonDisabled(value)}
                      audioRole="match"
                      sectionLabel="Match audio"
                    />
                  </AudioFieldsStack>
                ) : MatchComponent ? (
                  <MatchComponent
                    source={option?.match_item}
                    audio={option?.match_audio || ""}
                    value={option?.match_text}
                    matchType={question.matchType}
                    questionType={question.questionType}
                    optId={optId}
                    onTextChange={(v) => {
                      onchangeOptionMatch(index, optId, v, "match_text");
                    }}
                    imageClassName={` ${isTablate ? "assignmentTabImg" : ""}`}
                    containerClassName=""
                    style={{
                      marginTop: option?.match_item
                        ? (window.innerWidth >= 1024 ? "30px" : "85px")
                        : "",
                    }}
                    setButtonDisabled={(value) => setButtonDisabled(value)}
                    handlePicture={(v, audio) => {
                      setHasChanged(true);
                      if (question.matchType === "ImageAndAudio") {
                        const type =
                          audio === "audio" ? "match_audio" : "match_item";
                        onchangeOptionMatch(index, optId, v, type);
                      } else {
                        onchangeOptionMatch(index, optId, v, "match_item");
                      }
                    }}
                    showImagePicker={showImagePicker}
                    onMediaUploaded={onMediaUploaded}
                    audioRole="match"
                    sectionLabel="Match audio"
                  />
                ) : null}
              </div>
            </div>
          );
        })}
        <Button
          variant="contained"
          color="primary"
          disabled={disabled}
          onClick={() => onAddMoreOption(index)}
        >
          Add Question
        </Button>
      </div>
    </>
  );
};

export default memo(MatchMaking);
